/**
 * @file FontQuickPicks.tsx
 * @description Быстрый выбор в окне «Шрифт элемента» (phase19.16, партия SB120
 * «свобода выбора», этап 0; site-builder.md SB120; uiux-sb120-fields §4):
 * блоки «Шрифты сайта» (шрифты ролей оформления с подписью, чьи) и «Недавние»
 * (до 6, на устройстве, по магазину) над каталогом `FontCatalogBody`.
 *
 * Строки — те же `FontCatalogRow`, выбор только отмечает строку, применяется
 * кнопкой окна «Выбрать «…»» — как в каталоге. Своих шрифтов, которых нет в
 * реестре (удалены или список ещё не получен), здесь нет; в «Недавних» нет
 * шрифтов, уже показанных в «Шрифтах сайта». Пустой блок не рисуется.
 */

import { SITE_FONT_CATEGORY_LABELS, type SiteFontRegistry, type SiteTheme } from "@chaiberi/shared";

import { SectionLabel } from "../../ui/SectionLabel";
import { FontCatalogRow } from "../../site-view/appearance/FontCatalogRow";
import { FONT_SAMPLE_TEXT } from "../../site-view/appearance/FontCatalogList";
import { useRecentFonts } from "../../../hooks/site-fields/useRecentFonts";
import { FONT_CHIP_ROLES } from "../../../lib/site-fields/font-field-model";

interface Props {
  fonts: SiteTheme["fonts"];
  registry: SiteFontRegistry;
  chosen: string | null;
  onChoose: (key: string) => void;
}

interface QuickRow {
  key: string;
  meta: string;
}

/** Шрифты ролей: один ключ — одна строка, подпись — чьи («заголовки, основной»). */
function siteRows(fonts: SiteTheme["fonts"], registry: SiteFontRegistry): QuickRow[] {
  const roles = new Map<string, string[]>();
  for (const { role, label } of FONT_CHIP_ROLES) {
    const key = fonts[role];
    if (key === null || !registry.has(key)) continue;
    roles.set(key, [...(roles.get(key) ?? []), label.toLowerCase()]);
  }
  return [...roles].map(([key, labels]) => ({ key, meta: labels.join(", ") }));
}

export function FontQuickPicks({ fonts, registry, chosen, onChoose }: Props) {
  // Читаем при каждом открытии окна: выбор в соседнем поле «Шрифт» уже виден.
  const { recent } = useRecentFonts();
  const site = siteRows(fonts, registry);
  const recentRows: QuickRow[] = recent
    .filter((key) => registry.has(key) && !site.some((row) => row.key === key))
    .map((key) => {
      const entry = registry.find(key);
      return { key, meta: entry ? SITE_FONT_CATEGORY_LABELS[entry.category] : "" };
    });

  return (
    <>
      <QuickBlock title="Шрифты сайта" rows={site} registry={registry} chosen={chosen} onChoose={onChoose} />
      <QuickBlock title="Недавние" rows={recentRows} registry={registry} chosen={chosen} onChoose={onChoose} />
    </>
  );
}

interface BlockProps {
  title: string;
  rows: readonly QuickRow[];
  registry: SiteFontRegistry;
  chosen: string | null;
  onChoose: (key: string) => void;
}

function QuickBlock({ title, rows, registry, chosen, onChoose }: BlockProps) {
  if (rows.length === 0) return null;
  return (
    <section aria-label={title} className="flex flex-col border-b border-[var(--border)]">
      <SectionLabel as="h2" className="px-4 pb-2 pt-3">
        {title}
      </SectionLabel>
      <div role="radiogroup" aria-label={title}>
        {rows.map((row) => (
          <FontCatalogRow
            key={row.key}
            fontKey={row.key}
            family={registry.find(row.key)?.family ?? row.key}
            meta={row.meta}
            sample={FONT_SAMPLE_TEXT}
            selected={row.key === chosen}
            onSelect={() => onChoose(row.key)}
          />
        ))}
      </div>
    </section>
  );
}
